export const MEDICATIONS = {
    mounjaro: { label: "Mounjaro", doses: [2.5, 5, 7.5, 10, 12.5, 15], cadenceDays: 7 },
    zepbound: { label: "Zepbound", doses: [2.5, 5, 7.5, 10, 12.5, 15], cadenceDays: 7 },
    ozempic: { label: "Ozempic", doses: [0.25, 0.5, 1, 2], cadenceDays: 7 },
    wegovy: { label: "Wegovy", doses: [0.25, 0.5, 1, 1.7, 2.4], cadenceDays: 7 },
    rybelsus: { label: "Rybelsus", doses: [3, 7, 14], cadenceDays: 1 },
    saxenda: { label: "Saxenda", doses: [0.6, 1.2, 1.8, 2.4, 3], cadenceDays: 1 },
    trulicity: { label: "Trulicity", doses: [0.75, 1.5, 3, 4.5], cadenceDays: 7 },
    victoza: { label: "Victoza", doses: [0.6, 1.2, 1.8], cadenceDays: 1 },
    // Generic names, for a member on a compounded prescription rather than a
    // brand. A compounder writes what the prescriber asked for, so the ladder is
    // a shortcut and the dose field takes any amount.
    tirzepatide: { label: "Tirzepatide", doses: [2.5, 5, 7.5, 10, 12.5, 15], cadenceDays: 7 },
    semaglutide: { label: "Semaglutide", doses: [0.25, 0.5, 1, 1.7, 2.4], cadenceDays: 7 },
    liraglutide: { label: "Liraglutide", doses: [0.6, 1.2, 1.8, 2.4, 3], cadenceDays: 1 },
    // No ladder: a member on something unnamed types the amount instead.
    other: { label: "Other", doses: [], cadenceDays: 7 },
};
export const MEDICATION_KEYS = Object.keys(MEDICATIONS);
/**
 * How often a member takes it.
 *
 * Splitting a weekly dose across two injections is an established off-label
 * practice rather than a labelled regimen: no trial has evaluated it, and it
 * rests on the long half-lives that make weekly dosing work in the first place
 * (tirzepatide about 5 days, semaglutide about 1 week, both per the FDA
 * prescribing information). Members who do it inject roughly 3 to 4 days apart.
 *
 * 3.5 is the average of that spacing, not a claim that a member injects on the
 * half day. Reminders re-anchor on each logged dose, so the error does not
 * accumulate. The model that would fit the practice exactly is a weekly
 * schedule on named days, which is worth building before a second cadence
 * feature leans on this float.
 */
export const CADENCE_OPTIONS = [
    { days: 7, label: "Once a week" },
    { days: 3.5, label: "Twice a week" },
    { days: 14, label: "Every two weeks" },
    { days: 1, label: "Every day" },
];
export function medicationDoses(medication) {
    // An unknown name is the no-ladder case, the same as "Other": the caller
    // falls through to a free amount rather than offering another drug's steps.
    return MEDICATIONS[medication.toLowerCase()]?.doses ?? [];
}
export function medicationCadence(medication) {
    return MEDICATIONS[medication.toLowerCase()]?.cadenceDays ?? 7;
}
/** The label a member sees. Falls back to the key for anything logged earlier. */
export function medicationLabel(medication) {
    const spec = MEDICATIONS[medication.toLowerCase()];
    if (spec)
        return spec.label;
    return medication.charAt(0).toUpperCase() + medication.slice(1);
}
export const WEEKDAY_NAMES = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];
export const GLP1_SIDE_EFFECTS = [
    { id: "nausea", label: "Nausea" },
    { id: "low_appetite", label: "Low appetite" },
];
function localClock(now, timeZone) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    }).formatToParts(now);
    const part = (type) => parts.find((p) => p.type === type)?.value ?? "";
    const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(part("weekday"));
    return { weekday, minutes: Number(part("hour")) * 60 + Number(part("minute")) };
}
function weeklyDoseDay(doseDay, cadenceDays) {
    if (doseDay == null || !Number.isInteger(doseDay) || doseDay < 0 || doseDay > 6)
        return null;
    return Math.round(cadenceDays ?? 7) === 7 ? doseDay : null;
}
/** Days since the member's weekly dose day in their own zone: 0 on the day. Null without a weekly dose day. */
export function daysSinceDoseDay(doseDay, cadenceDays, now, timeZone) {
    const day = weeklyDoseDay(doseDay, cadenceDays);
    if (day == null)
        return null;
    return (localClock(now, timeZone).weekday - day + 7) % 7;
}
/** 9am local on the member's next dose day, for a reminder with no logged dose to count from. */
export function nextDoseDayAt(doseDay, cadenceDays, now, timeZone) {
    const day = weeklyDoseDay(doseDay, cadenceDays);
    if (day == null)
        return null;
    const { weekday } = localClock(now, timeZone);
    const [y = 0, m = 1, d = 1] = new Intl.DateTimeFormat("en-CA", { timeZone })
        .format(now)
        .split("-")
        .map(Number);
    const wall = Date.UTC(y, m - 1, d + ((day - weekday + 7) % 7), 9);
    // Correct by the zone offset at the target instant, so a DST change between now and then cannot shift it.
    let at = wall;
    for (let i = 0; i < 2; i++)
        at = wall - zoneOffsetMs(new Date(at), timeZone);
    return new Date(at);
}
function zoneOffsetMs(at, timeZone) {
    const p = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    }).formatToParts(at);
    const n = (type) => Number(p.find((x) => x.type === type)?.value);
    const local = Date.UTC(n("year"), n("month") - 1, n("day"), n("hour"), n("minute"), n("second"));
    return local - Math.floor(at.getTime() / 1000) * 1000;
}
//# sourceMappingURL=glp1.js.map